import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n";
import { api, formatApiError } from "../lib/api";

const LOGO_URL = "https://www.perfinco.pt/wp-content/uploads/2022/07/perfinco-logo.png";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useT();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login"); // login | forgot
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  useEffect(() => {
    if (user && typeof user === "object") {
      navigate(user.role === "accountant" ? "/contabilista" : "/cliente", { replace: true });
    }
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "accountant" ? "/contabilista" : "/cliente");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const onForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail }, { skipAuthRefresh: true });
      setForgotSent(true);
    } catch (err) {
      setForgotSent(true); // never reveal whether the email exists
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen paper-grain flex items-center justify-center p-4">
      <div className="w-full max-w-md fade-in">
        <Link to="/" className="flex items-center gap-3 justify-center mb-8" data-testid="login-brand-link">
          <img src={LOGO_URL} alt="Perfinco" className="h-8" />
          <span className="h-6 w-px bg-[color:var(--border)]" />
          <span className="font-display text-sm font-medium tracking-[0.18em] uppercase text-[color:var(--ink-muted)]">{t("brand.suffix")}</span>
        </Link>

        <div className="card-soft">
          {mode === "login" ? (
            <>
              <span className="label-eyebrow">{t("login.eyebrow")}</span>
              <h1 className="font-display text-2xl sm:text-3xl mt-2 mb-6">{t("login.title")}</h1>

              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <label className="label-eyebrow block mb-2">{t("login.email")}</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-soft"
                    required
                    data-testid="login-email-input"
                    autoComplete="email"
                  />
                </div>
                <div>
                  <label className="label-eyebrow block mb-2">{t("login.password")}</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-soft"
                    required
                    data-testid="login-password-input"
                    autoComplete="current-password"
                  />
                </div>
                {error && (
                  <div className="text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] rounded-lg p-3" data-testid="login-error">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full"
                  data-testid="login-submit-btn"
                >
                  {loading ? t("login.submitting") : t("login.submit")}
                </button>
              </form>

              <div className="mt-6 flex items-center gap-3">
                <span className="flex-1 h-px bg-[color:var(--border)]" />
                <span className="text-xs text-[color:var(--ink-muted)] uppercase tracking-wider">{t("login.or")}</span>
                <span className="flex-1 h-px bg-[color:var(--border)]" />
              </div>

              <button
                type="button"
                onClick={() => {
                  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                  const redirectUrl = window.location.origin + "/cliente";
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                className="mt-4 w-full inline-flex items-center justify-center gap-3 rounded-lg border border-[color:var(--border)] bg-white px-4 py-2.5 text-sm font-medium hover:bg-[color:var(--paper)] transition-colors"
                data-testid="google-login-btn"
              >
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                </svg>
                {t("login.google")}
              </button>

              <div className="mt-4 text-sm">
                <button
                  type="button"
                  onClick={() => { setMode("forgot"); setForgotEmail(email); setError(""); }}
                  className="text-[color:var(--primary)] hover:underline underline-offset-4"
                  data-testid="forgot-password-link"
                >
                  Esqueci-me da palavra-passe
                </button>
              </div>

              <div className="mt-6 text-sm text-[color:var(--ink-muted)]">
                {t("login.no_account")}
              </div>
            </>
          ) : (
            <>
              <span className="label-eyebrow">recuperar acesso</span>
              <h1 className="font-display text-2xl sm:text-3xl mt-2 mb-2">Esqueceu-se da palavra-passe?</h1>
              {forgotSent ? (
                <div data-testid="forgot-sent">
                  <p className="text-sm text-[color:var(--ink-muted)] mt-3">
                    Se existir uma conta com esse email, enviámos um link de recuperação. Verifique a sua caixa de entrada (e a pasta de spam). O link é válido por 1 hora.
                  </p>
                  <button type="button" onClick={() => { setMode("login"); setForgotSent(false); }} className="btn-primary w-full mt-5" data-testid="forgot-back-login-btn">
                    Voltar ao início de sessão
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-sm text-[color:var(--ink-muted)] mt-2 mb-6">Indique o seu email e enviamos-lhe um link para definir uma nova palavra-passe.</p>
                  <form onSubmit={onForgot} className="space-y-4">
                    <div>
                      <label className="label-eyebrow block mb-2">Email</label>
                      <input
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        className="input-soft"
                        required
                        data-testid="forgot-email-input"
                        autoComplete="email"
                      />
                    </div>
                    <button type="submit" disabled={forgotLoading} className="btn-primary w-full" data-testid="forgot-submit-btn">
                      {forgotLoading ? "A enviar…" : "Enviar link de recuperação"}
                    </button>
                  </form>
                  <div className="mt-4 text-sm">
                    <button type="button" onClick={() => { setMode("login"); setError(""); }} className="text-[color:var(--primary)] hover:underline underline-offset-4" data-testid="back-to-login-link">
                      ← Voltar ao início de sessão
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
